import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { resolvePublicAssetUrl } from '../../services/api';

function CategoryNoteList({ entries }) {
  if (!entries?.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
      {entries.map((entry, idx) => (
        <Box component="li" key={`${entry.sessionDate}-${idx}`} sx={{ mb: 0.75 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            <Typography component="span" variant="body2" color="text.secondary">
              {entry.sessionDate}
              {entry.level ? ` · ${entry.level}` : ''}
              {' — '}
            </Typography>
            {entry.notes}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function formatSessionDate(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || 'Class';
  return new Date(`${s}T12:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fileLabel(url, fallback) {
  const name = String(url || '')
    .split('/')
    .filter(Boolean)
    .pop();
  if (!name) return fallback;
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

function HomeworkGivenList({ items }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" fontWeight={600}>
        Homework given ({items.length})
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {items.map((item) => {
          const preview = item.message
            ? String(item.message).replace(/\s+/g, ' ').trim()
            : item.pdfUrl
              ? fileLabel(item.pdfUrl, 'Homework PDF')
              : item.images?.length
                ? item.images.length === 1
                  ? 'Screenshot'
                  : `${item.images.length} screenshots`
                : 'Marked as sent';
          return (
            <Accordion
              key={item.sessionId || `${item.sessionDate}-${preview}`}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 1.5, '& .MuiAccordionSummary-content': { minWidth: 0, my: 1 } }}
              >
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={700}>
                      {formatSessionDate(item.sessionDate)}
                    </Typography>
                    {item.message ? <Chip size="small" label="Text" variant="outlined" /> : null}
                    {item.pdfUrl ? <Chip size="small" label="PDF" variant="outlined" /> : null}
                    {item.images?.length ? (
                      <Chip
                        size="small"
                        label={item.images.length === 1 ? 'Screenshot' : `${item.images.length} screenshots`}
                        variant="outlined"
                      />
                    ) : null}
                    {item.dueDate ? (
                      <Typography variant="caption" color="text.secondary">
                        Due {formatSessionDate(item.dueDate)}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {preview}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5, bgcolor: 'grey.50' }}>
                <Stack spacing={1.25}>
                  {item.message ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>
                      {item.message}
                    </Typography>
                  ) : null}
                  {item.pdfUrl ? (
                    <Link
                      href={resolvePublicAssetUrl(item.pdfUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      fontWeight={700}
                    >
                      {fileLabel(item.pdfUrl, 'Open homework PDF')}
                    </Link>
                  ) : null}
                  {item.images?.length ? (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {item.images.map((img, idx) => (
                        <Link
                          key={img.id || idx}
                          href={resolvePublicAssetUrl(img.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="none"
                        >
                          <Box
                            component="img"
                            src={resolvePublicAssetUrl(img.url)}
                            alt={img.name || `Homework screenshot ${idx + 1}`}
                            sx={{
                              width: 96,
                              height: 96,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              display: 'block',
                            }}
                          />
                        </Link>
                      ))}
                    </Stack>
                  ) : null}
                  {!item.message && !item.pdfUrl && !item.images?.length ? (
                    <Typography variant="body2" color="text.secondary">
                      Marked as sent. No text or file is saved on this class.
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

function CategoryBlock({ label, sessionCount, entries }) {
  if (!sessionCount && !entries?.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {label}
        {sessionCount ? ` (${sessionCount})` : ''}
      </Typography>
      <CategoryNoteList entries={entries} />
    </Box>
  );
}

/** Batch teaching section: categories with note details nested underneath. */
export function MonthlyReportBatchTeaching({ batch }) {
  if (!batch) return null;
  const sessions = batch.sessions || [];
  const scheduled =
    batch.sessionsScheduled != null ? Number(batch.sessionsScheduled) : sessions.length;
  const delivered =
    batch.sessionsDelivered != null
      ? Number(batch.sessionsDelivered)
      : sessions.filter((s) => s.isDelivered).length;

  const homeworkGiven = batch.homeworkGiven || [];
  const hasContent =
    (batch.moduleRollup || []).length > 0 ||
    (batch.miscRemarks || []).length > 0 ||
    (batch.nextPlanRollup || []).length > 0 ||
    homeworkGiven.length > 0;

  return (
    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
      <Typography fontWeight={600}>{batch.batchName}</Typography>
      <Typography variant="body2" color="text.secondary">
        {delivered} delivered · {scheduled} scheduled · {batch.studentCount} students
      </Typography>

      {!hasContent ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {delivered === 0
            ? 'No sessions delivered yet this month.'
            : 'No class notes this month.'}
        </Typography>
      ) : (
        <>
          {(batch.moduleRollup || []).map((mod) => (
            <CategoryBlock
              key={mod.type}
              label={mod.label}
              sessionCount={mod.sessionCount}
              entries={mod.entries}
            />
          ))}

          {(batch.miscRemarks || []).length > 0 && (
            <CategoryBlock label="Other remarks" entries={batch.miscRemarks} />
          )}

          <HomeworkGivenList items={homeworkGiven} />

          {(batch.nextPlanRollup || []).length > 0 && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Next class plans (from sessions)
              </Typography>
              {batch.nextPlanRollup.map((mod) => (
                <CategoryBlock
                  key={mod.type}
                  label={mod.label}
                  sessionCount={mod.sessionCount}
                  entries={mod.entries}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
